/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, IconButton, Popover, TextField, Theme, Typography } from '@mui/material';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import React, { useState } from 'react';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { SVGSprite } from '../svg-sprite';
import { ChatbotData } from '../../models/chat-message';
import { CSVButton } from '../../shared/components/csv-button';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import TaskIcon from '@mui/icons-material/Task';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            justifyContent: 'flex-end',
            marginRight: '2em'
        },
        chatContainer: {
            width: 'calc(100vw - 2em)',
            margin: 'auto',
            padding: '1em',
            height: '90.5vh'
        },
        formContainer: {
            marginTop: '1em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5em'
        },
        formInput: {
            flex: 1,
            maxHeight: '5.8em',
            '& .MuiOutlinedInput-root': {
                maxHeight: '5.8em'
            }
        },
        chatTitle: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '0.3em'
        },
        chatButton: {
            borderRadius: '50%',
            padding: '1em',
            height: '4.5em',
            [theme.breakpoints.between('xs', 'sm')]: {
                transform: 'scale(0.8)'
            },
        },
        chatList: {
            background: theme.palette.background.default,
            border: '1px solid #ccc',
            padding: '0.5em',
            minHeight: '300px',
            height: '80%',
            overflowY: 'auto'
        },
        uploadButton: {
            transform: 'scale(1.5)',
            margin: '0 1em',
            minWidth: 'auto',
        },
        sendButton: {}
    });

interface Props extends WithStyles<typeof styles> {
    onMessageSent: (message: string, file?: File) => Promise<ChatbotData>;
    className?: string;
    isLoading?: boolean;
}

const ChatBox: React.FC<Props> = (props) => {
    const { classes, onMessageSent } = props;
    const [messages, setMessages] = useState([] as any);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [fileData, setFileData] = useState<File | undefined>(undefined);

    const sendMessage = async () => {
        if (!input.trim()) {
            return;
        }

        const newMessages = [...messages, { role: 'user', content: input }];
        setMessages(newMessages);
        setInput('');
        setIsLoading(true);

        let partial = '';
        onMessageSent(input, fileData).then(
            (response) => {
                // Update last bot message dynamically
                const { message } = response;
                partial += message;
                setMessages([...newMessages, { role: 'assistant', content: partial }]);
                setIsLoading(false);
            },
        );
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setFileData(undefined);
    };

    const onCSVImport = (parsedCSVFileData: string[][], csvFile?: unknown) => {
        setFileData(csvFile as File);
    };

    const onClearImport = () => {
        setFileData(undefined);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'chat-popover' : undefined;
    const chatBtnColor = open ? 'primary' : 'secondary';

    return (
        <div className={classes.root}>
            <Button
                className={classes.chatButton}
                variant="contained"
                aria-describedby={id}
                aria-label="Open chat"
                color={chatBtnColor}
                onClick={handleClick}
            >
                {
                    open ? <CloseIcon />
                        : <ChatIcon />
                } 
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <div className={classes.chatContainer}>
                    <Typography
                        className={classes.chatTitle}
                        variant="h4"
                    >
                        <SVGSprite name="default" title="CC Bot" /> AI Chat
                    </Typography>
                    <div className={classes.chatList}>
                        {messages.map((message: any, index: number) => (
                            <div key={index} style={{ margin: '0.5em 0' }}>
                                <b>{message.role === 'user' ? 'You' : 'CC Bot'}:</b> {message.content}
                            </div>
                        ))}
                        {isLoading && <div><i>Thinking...</i></div>}
                    </div>

                    <div className={classes.formContainer}>
                        <TextField
                            className={classes.formInput}
                            label="Ask anything"
                            variant="outlined"
                            value={input}
                            multiline
                            maxRows={4}
                            onChange={(e) => setInput(e.target.value)}
                        />
                        {
                            fileData ?
                                <TaskIcon
                                    color="secondary"
                                    className={classes.uploadButton}
                                />
                                : <CSVButton
                                    classes={{
                                        button: classes.uploadButton
                                    }}
                                    size="large"
                                    color="secondary"
                                    importData={true}
                                    onCSVImport={onCSVImport}
                                >
                                    <NoteAddIcon />
                                </CSVButton>
                        }
                    </div>
                    <div className={classes.formContainer}>
                        <Button
                            className={classes.sendButton}
                            variant="contained"
                            color="primary"
                            onClick={sendMessage}
                        >
                            Send
                        </Button>
                        {
                            fileData && (
                                <Typography variant="body2">
                                    Attached file: {fileData.name}
                                    <IconButton
                                        aria-label="clear"
                                        onClick={onClearImport}
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                </Typography>
                            )
                        }
                    </div>
                </div>
            </Popover>
        </div>
        
    );
};

const MUIComponent = withStyles(styles)(ChatBox);
export { MUIComponent as ChatBox };
